export type DailySummaryRow = {
  fmbThaliId?: string;
  name?: string;
  thaliNo?: string;
  thaliType?: string;
  fileNo?: string;
  deliveryAddress?: string;
  deliveryMohallah?: string;
  profileCode?: string;
  profileName?: string;
};

export type DailySummarySortColumn = "thali" | "fileNo" | "name" | "address" | "schedule";

export function rowThaliLabel(row: DailySummaryRow): string {
  const base = (row.thaliNo ?? "").trim();
  const tt = (row.thaliType ?? "").trim();
  return tt ? `${base} (${tt})` : base;
}

export function rowAddress(row: DailySummaryRow): string {
  return row.deliveryAddress || row.deliveryMohallah
    ? [row.deliveryAddress, row.deliveryMohallah].filter(Boolean).join(" — ")
    : "";
}

export function rowSchedule(row: DailySummaryRow): string {
  return row.profileCode && row.profileName ? `${row.profileCode} — ${row.profileName}` : "";
}

export function dailySummarySortKey(row: DailySummaryRow, col: DailySummarySortColumn): string {
  switch (col) {
    case "thali":
      return rowThaliLabel(row).toLowerCase();
    case "fileNo":
      return (row.fileNo ?? "").toLowerCase();
    case "name":
      return (row.name ?? "").toLowerCase();
    case "address":
      return rowAddress(row).toLowerCase();
    case "schedule":
      return rowSchedule(row).toLowerCase();
    default:
      return "";
  }
}

export function dailySummarySearchHaystack(row: DailySummaryRow): string {
  return [rowThaliLabel(row), row.fileNo, row.name, rowAddress(row), rowSchedule(row)]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function filterDailySummaryRows(rows: DailySummaryRow[], search: string): DailySummaryRow[] {
  const t = search.trim().toLowerCase();
  if (!t) return rows;
  return rows.filter((row) => dailySummarySearchHaystack(row).includes(t));
}

export const DAILY_SUMMARY_EMPTY = {
  loading: "Loading…",
  noMenu: "No deliveries for this date.",
  none: "No deliveries expected. Thalis may be inactive, stopped, or not on a service day.",
  filter: "No rows match this filter.",
} as const;

export function dailySummaryEmptyMessage(params: {
  loading: boolean;
  hasError: boolean;
  hasPayload: boolean;
  hasDailyMenu: boolean;
  rowCount: number;
  visibleCount: number;
}): string | null {
  if (params.loading) return DAILY_SUMMARY_EMPTY.loading;
  if (params.hasError || !params.hasPayload) return null;
  if (!params.hasDailyMenu) return DAILY_SUMMARY_EMPTY.noMenu;
  if (params.rowCount === 0) return DAILY_SUMMARY_EMPTY.none;
  if (params.visibleCount === 0) return DAILY_SUMMARY_EMPTY.filter;
  return null;
}

export function sortDailySummaryRows(
  rows: DailySummaryRow[],
  sortBy: DailySummarySortColumn,
  sortDir: "asc" | "desc"
): DailySummaryRow[] {
  const dir = sortDir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = dailySummarySortKey(a, sortBy);
    const vb = dailySummarySortKey(b, sortBy);
    const cmp = va.localeCompare(vb, undefined, { numeric: true, sensitivity: "base" });
    if (cmp !== 0) return cmp * dir;
    return String(a.fmbThaliId ?? "").localeCompare(String(b.fmbThaliId ?? ""));
  });
}
